import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Loader2, X, Users, Building, Briefcase, ArrowRight, Sparkles } from 'lucide-react';
import { api } from '../../lib/api';

const SS_KEY_QUERY = 'tp_global_search_q';
const SS_KEY_SCOPE = 'tp_global_search_scope';

/**
 * Recherche globale admin.
 * - Persiste la query/scope dans sessionStorage → ne se perd pas après navigation.
 * - Scope "skills" : ne match QUE sur compétences/outils/profession (pas sur les noms).
 * - Affiche un snippet montrant où le match a eu lieu.
 * - Lien "Voir tous les résultats" → /admin/candidates?q=...
 */
const GlobalSearchBar = () => {
  // Restore from session on mount
  const [query, setQuery] = useState(() => {
    if (typeof window === 'undefined') return '';
    return sessionStorage.getItem(SS_KEY_QUERY) || '';
  });
  const [scope, setScope] = useState(() => {
    if (typeof window === 'undefined') return 'all';
    return sessionStorage.getItem(SS_KEY_SCOPE) || 'all';
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const runSearch = useCallback(async (q, sc) => {
    if (!q || q.trim().length < 2) {
      setResults(null);
      return;
    }
    setLoading(true);
    try {
      const url = `/admin/search?q=${encodeURIComponent(q.trim())}&limit=5&scope=${sc}`;
      const res = await api.get(url);
      if (res.ok) {
        const data = await res.json();
        setResults(data);
        setOpen(true);
      }
    } catch {
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Persist query + run debounced search
  useEffect(() => {
    sessionStorage.setItem(SS_KEY_QUERY, query);
    sessionStorage.setItem(SS_KEY_SCOPE, scope);

    if (!query || query.trim().length < 2) {
      setResults(null);
      return;
    }
    const t = setTimeout(() => runSearch(query, scope), 300);
    return () => clearTimeout(t);
  }, [query, scope, runSearch]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Re-open dropdown when navigating BACK to a route with stored query
  // (e.g. user clicked a result then pressed browser back)
  useEffect(() => {
    if (query && results) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const goTo = (path) => {
    setOpen(false);
    // KEEP the query in sessionStorage — only collapse the dropdown
    navigate(path);
  };

  const goToAllResults = () => {
    if (!query) return;
    setOpen(false);
    navigate(`/admin/candidates?q=${encodeURIComponent(query.trim())}&scope=${scope}`);
  };

  const clear = () => {
    setQuery('');
    setResults(null);
    setOpen(false);
    sessionStorage.removeItem(SS_KEY_QUERY);
    inputRef.current?.focus();
  };

  const totalResults = results
    ? (results.candidates?.length || 0) + (results.clients?.length || 0) + (results.missions?.length || 0)
    : 0;
  const totalCandidatesAll = results?.totals?.candidates || 0;

  return (
    <div ref={containerRef} className="relative w-full max-w-md" data-testid="global-search-bar">
      <div className="relative">
        {loading ? (
          <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-tp-text-light animate-spin" />
        ) : (
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-tp-text-light" />
        )}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results && setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') goToAllResults();
            if (e.key === 'Escape') setOpen(false);
          }}
          placeholder="Rechercher (n8n, claude, community manager...)"
          className="w-full pl-9 pr-9 py-2 text-sm bg-white border border-tp-border rounded-md focus:outline-none focus:ring-2 focus:ring-tp-primary/30 focus:border-tp-primary"
          data-testid="global-search-input"
        />
        {query && (
          <button
            onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-tp-bg rounded"
            data-testid="global-search-clear"
          >
            <X className="h-3.5 w-3.5 text-tp-text-light" />
          </button>
        )}
      </div>

      {open && results && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-tp-border rounded-md shadow-lg z-50 max-h-[70vh] overflow-y-auto" data-testid="global-search-results">
          {/* Scope toggle */}
          <div className="flex items-center gap-1 p-2 border-b border-tp-border/50 bg-tp-bg/30 sticky top-0">
            <button
              onClick={() => setScope('all')}
              className={`text-[11px] px-2 py-1 rounded transition ${scope === 'all' ? 'bg-tp-primary text-white' : 'text-tp-text-light hover:bg-white'}`}
              data-testid="scope-all"
            >
              Tout
            </button>
            <button
              onClick={() => setScope('skills')}
              className={`text-[11px] px-2 py-1 rounded transition ${scope === 'skills' ? 'bg-tp-primary text-white' : 'text-tp-text-light hover:bg-white'}`}
              data-testid="scope-skills"
              title="Cherche uniquement dans compétences, outils, profession (exclut les noms)"
            >
              <Sparkles className="h-3 w-3 inline -mt-0.5 mr-0.5" />
              Compétences uniquement
            </button>
          </div>

          {totalResults === 0 ? (
            <p className="text-sm text-tp-text-light italic p-4 text-center" data-testid="global-search-empty">
              Aucun résultat pour « {query} »{scope === 'skills' && ' (en compétences)'}
            </p>
          ) : (
            <div className="py-1">
              {results.candidates?.length > 0 && (
                <Section
                  icon={<Users className="h-3.5 w-3.5" />}
                  title="Candidats"
                  count={results.candidates.length}
                  total={totalCandidatesAll}
                >
                  {results.candidates.map((c) => (
                    <ResultRow
                      key={`cand-${c.id}`}
                      title={c.name}
                      subtitle={[c.subtitle, c.country].filter(Boolean).join(' · ')}
                      snippet={c.match_snippet}
                      badge={c.status}
                      onClick={() => goTo(`/admin/candidates/${c.id}`)}
                      testId={`search-result-cand-${c.id}`}
                    />
                  ))}
                  {totalCandidatesAll > results.candidates.length && (
                    <button
                      onClick={goToAllResults}
                      className="w-full text-left px-3 py-2 hover:bg-tp-primary/5 flex items-center justify-between gap-2 border-t border-tp-border/30 text-tp-primary"
                      data-testid="see-all-candidates"
                    >
                      <span className="text-xs font-medium">
                        Voir les {totalCandidatesAll} résultats candidats
                      </span>
                      <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  )}
                </Section>
              )}
              {results.clients?.length > 0 && (
                <Section icon={<Building className="h-3.5 w-3.5" />} title="Clients" count={results.clients.length}>
                  {results.clients.map((c) => (
                    <ResultRow
                      key={`cli-${c.id}`}
                      title={c.name}
                      subtitle={c.subtitle}
                      onClick={() => goTo(`/admin/clients/${c.id}`)}
                      testId={`search-result-cli-${c.id}`}
                    />
                  ))}
                </Section>
              )}
              {results.missions?.length > 0 && (
                <Section icon={<Briefcase className="h-3.5 w-3.5" />} title="Missions" count={results.missions.length}>
                  {results.missions.map((m) => (
                    <ResultRow
                      key={`mis-${m.id}`}
                      title={m.title}
                      subtitle={m.subtitle}
                      badge={m.status}
                      onClick={() => goTo(`/admin/missions/${m.id}`)}
                      testId={`search-result-mis-${m.id}`}
                    />
                  ))}
                </Section>
              )}
            </div>
          )}
          <div className="p-2 text-[10px] text-tp-text-light text-center bg-tp-bg/30 border-t border-tp-border/50">
            Entrée = voir tous les résultats · Esc = fermer
          </div>
        </div>
      )}
    </div>
  );
};

const Section = ({ icon, title, count, total, children }) => (
  <div className="border-b border-tp-border/50 last:border-0">
    <div className="flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wide font-semibold text-tp-text-light bg-tp-bg/20">
      {icon}
      <span>{title}</span>
      <span className="ml-auto">
        {count}{total > count ? ` / ${total}` : ''}
      </span>
    </div>
    <div>{children}</div>
  </div>
);

const ResultRow = ({ title, subtitle, snippet, badge, onClick, testId }) => (
  <button
    onClick={onClick}
    className="w-full text-left px-3 py-2 hover:bg-tp-primary/5 flex items-start justify-between gap-2"
    data-testid={testId}
  >
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-tp-text truncate">{title}</p>
      {subtitle && <p className="text-xs text-tp-text-light truncate">{subtitle}</p>}
      {snippet && (
        <p className="text-[11px] text-emerald-700 mt-0.5 truncate">
          <Sparkles className="h-2.5 w-2.5 inline mr-0.5" />
          {snippet}
        </p>
      )}
    </div>
    {badge && (
      <span className="text-[10px] uppercase font-semibold tracking-wide text-tp-text-light bg-tp-bg px-1.5 py-0.5 rounded shrink-0">
        {badge}
      </span>
    )}
  </button>
);

export default GlobalSearchBar;
